import { GOAL_CARD_TEXT, GOAL_DIAGRAMS, type GoalCardId } from '@anqi/shared'
import { MiniBoard } from './MiniBoard'
import { seatStyle } from './Board'

/**
 * 游戏说明。**对局中随时能打开**，这一点比说明写得多好更重要：
 * 三个人不能通话，谁忘了「散布」到底要几颗子，只能自己查 ——
 * 查不到就只能瞎下，或者打断另外两个人去问。
 *
 * 六张卡的示意图不是手画的：数据来自 shared 的 GOAL_DIAGRAMS，
 * 被服务端测试用真实判定函数验证过（见 tests/goal-diagrams.test.ts），
 * 所以「照着图摆一定算达成」这句话是可以保证的。
 */
const ORDER: GoalCardId[] = ['twinLines', 'corner', 'traverse', 'siege', 'scatter', 'island']

export function RulesSheet({ onClose }: { onClose(): void }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-slate-900">
      {/* 关闭按钮吸顶：说明很长，滚到一半想退出不该被迫滚回去 */}
      <div className="sticky top-0 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-3">
        <span className="text-base font-bold">游戏说明</span>
        <button
          type="button"
          onClick={onClose}
          className="min-h-[44px] min-w-[64px] rounded-lg bg-slate-100 text-sm font-bold text-slate-900"
        >
          关闭
        </button>
      </div>

      <div className="space-y-5 px-4 py-4 text-sm leading-relaxed">
        <Section title="一句话">
          <p>
            三个人，每人一张<strong className="text-amber-300">只有自己看得见</strong>的目标卡。
            轮流在棋盘上放子，谁先做成自己那张卡，谁赢。
          </p>
          <p className="text-slate-400">
            难点在于：你要一边做自己的，一边猜另外两个人在做什么 ——
            因为挡住别人和做成自己一样有用。
          </p>
        </Section>

        <Section title="三家的棋子">
          <div className="flex gap-4">
            {[0, 1, 2].map((s) => {
              const st = seatStyle(s as 0 | 1 | 2)
              return (
                <span key={s} className="flex items-center gap-1">
                  <span className={`text-lg ${st.text}`}>{st.glyph}</span>
                  <span className="text-slate-300">{st.label}方</span>
                </span>
              )
            })}
            <span className="flex items-center gap-1">
              <span className="text-lg text-slate-500">✕</span>
              <span className="text-slate-300">禁区</span>
            </span>
          </div>
          <p className="text-slate-400">形状和颜色都不同，色弱也能分清谁是谁。</p>
        </Section>

        <Section title="轮到你时，可以做四件事之一">
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              <strong>放一颗子</strong>。任意空格。这是主要动作。
            </li>
            <li>
              <strong>放一个禁区标记</strong>（✕）。那一格谁都不能再用，永久有效。
              每人只有 2 个，用来堵别人的关键位置。
            </li>
            <li>
              <strong>指控某个人</strong>，猜他的目标卡。猜对了他要重新抽一张
              （之前的进度白费）；<strong className="text-red-300">猜错了你停一手</strong>。
              这是代价最大的一步。
            </li>
            <li>
              <strong>亮牌探宝</strong>。把自己的目标卡永久公开给两个人，换 2 个额外禁区标记。
              一局只能用一次，不可撤销。
            </li>
          </ol>
          <p className="text-slate-400">
            落子和放禁区都要点两下（先选格、再确认）—— 手机上误触一次就白扔一手。
          </p>
        </Section>

        <Section title="六张目标卡">
          <p className="text-slate-400">
            开局时三个人各抽一张，互不相同。下面每张图都是一个
            <strong className="text-amber-300">真的算达成</strong>的局面，照着摆就赢。
          </p>
          <div className="space-y-5">
            {ORDER.map((id) => (
              <GoalEntry key={id} id={id} />
            ))}
          </div>
        </Section>

        <Section title="什么时候结束">
          <ul className="list-disc space-y-1 pl-5">
            <li>有人做成自己的目标卡 —— 他赢。</li>
            <li>三个人的棋子都放完了 —— 比完成度，最高的赢。</li>
            <li>打满 45 回合 —— 同样比完成度。</li>
          </ul>
          <p className="text-slate-400">
            结束后三张卡全部公开，还会显示每个人的完成度 —— 输的人能看到自己差多少。
          </p>
        </Section>

        <button
          type="button"
          onClick={onClose}
          className="min-h-[52px] w-full rounded-lg bg-amber-500 text-base font-bold text-slate-900"
        >
          看完了，回去下棋
        </button>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-bold text-amber-300">{title}</h2>
      {children}
    </section>
  )
}

function GoalEntry({ id }: { id: GoalCardId }) {
  const text = GOAL_CARD_TEXT[id]
  const diagram = GOAL_DIAGRAMS[id]
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3">
      <p className="font-bold">{text.name}</p>
      <p className="mt-1 text-slate-300">{text.description}</p>
      <div className="mt-3 flex flex-col items-center gap-2">
        <MiniBoard diagram={diagram} />
        <p className="text-xs text-slate-400">{diagram.caption}</p>
      </div>
    </div>
  )
}
